import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import type { RolePlayAssessment } from "@/lib/powtoon-be/types";
import { DEFAULT_MAX_TURNS, MAX_TURNS_MAX, MAX_TURNS_MIN, newMetricId } from "../schema";
import { StringListEditor } from "./StringListEditor";

export function AssessmentCriteriaEditor({
  value,
  onChange,
  terminationRequired,
}: {
  value: RolePlayAssessment;
  onChange: (next: RolePlayAssessment) => void;
  terminationRequired?: boolean;
}) {
  const set = <K extends keyof RolePlayAssessment>(
    key: K,
    next: RolePlayAssessment[K],
  ) => onChange({ ...value, [key]: next });

  function updateMetric(
    i: number,
    patch: Partial<RolePlayAssessment["competency_metrics"][number]>,
  ) {
    set(
      "competency_metrics",
      value.competency_metrics.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    );
  }
  function removeMetric(i: number) {
    set("competency_metrics", value.competency_metrics.filter((_, idx) => idx !== i));
  }
  function moveMetric(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= value.competency_metrics.length) return;
    const next = [...value.competency_metrics];
    [next[i], next[j]] = [next[j], next[i]];
    set("competency_metrics", next);
  }

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Assessment criteria
      </p>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Competency metrics
          </span>
          <span className="text-[10px] text-muted-foreground">
            {value.competency_metrics.length}
          </span>
        </div>
        {value.competency_metrics.length === 0 && (
          <p className="text-xs italic text-muted-foreground">
            Add metrics the avatar should evaluate (e.g. empathy, accuracy).
          </p>
        )}
        {value.competency_metrics.map((m, i) => (
          <div key={m.id} className="flex items-start gap-1">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
              <Input
                value={m.name}
                onChange={(e) => updateMetric(i, { name: e.target.value })}
                placeholder="Metric name"
                className="h-8 text-sm sm:self-start"
              />
              <AutoGrowTextarea
                value={m.description ?? ""}
                onChange={(e) => updateMetric(i, { description: e.target.value })}
                placeholder="Description (optional)"
                className="min-h-8 text-sm"
              />
            </div>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground"
              disabled={i === 0} onClick={() => moveMetric(i, -1)} aria-label="Move up">
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground"
              disabled={i === value.competency_metrics.length - 1} onClick={() => moveMetric(i, 1)} aria-label="Move down">
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => removeMetric(i)} aria-label="Remove">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button
          type="button" variant="ghost" size="sm"
          onClick={() =>
            set("competency_metrics", [
              ...value.competency_metrics,
              { id: newMetricId(), name: "", description: "" },
            ])
          }
          className="h-7 px-2 text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add metric
        </Button>
      </div>

      <StringListEditor
        label="Success triggers"
        items={value.success_triggers}
        onChange={(next) => set("success_triggers", next)}
        placeholder="e.g. learner acknowledges concern"
        addLabel="Add trigger"
      />
      <StringListEditor
        label="Red flags"
        items={value.red_flags}
        onChange={(next) => set("red_flags", next)}
        placeholder="e.g. learner becomes defensive"
        addLabel="Add red flag"
      />
      <div className={terminationRequired && value.termination_conditions.filter((t) => t.trim()).length === 0 ? "rounded ring-1 ring-destructive/60 p-1.5 -m-1.5" : undefined}>
        <StringListEditor
          label="Termination conditions *"
          items={value.termination_conditions}
          onChange={(next) => set("termination_conditions", next)}
          placeholder="e.g. learner offers a refund and customer accepts"
          addLabel="Add condition"
          emptyHint="At least one is required."
        />
        <p className="mt-1 text-[11px] text-muted-foreground">
          Describe specific moments that end the scene — include a success and a failure/deadlock ending.
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="rp-max-turns" className="text-xs font-medium text-muted-foreground">
          Max turns
        </label>
        <Input
          id="rp-max-turns"
          type="number"
          min={MAX_TURNS_MIN}
          max={MAX_TURNS_MAX}
          value={value.max_turns ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (!raw) {
              const { max_turns: _drop, ...rest } = value;
              onChange(rest);
              return;
            }
            const n = Number(raw);
            if (Number.isFinite(n)) {
              set("max_turns", Math.min(MAX_TURNS_MAX, Math.max(MAX_TURNS_MIN, Math.round(n))));
            }
          }}
          placeholder={`${DEFAULT_MAX_TURNS} (default)`}
          className="h-8 w-40 text-sm"
        />
        <p className="text-[11px] text-muted-foreground">
          Learner turns before the avatar wraps up in character ({MAX_TURNS_MIN}–{MAX_TURNS_MAX}).
        </p>
      </div>
    </div>
  );
}